import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Box, Typography, Grid, useMediaQuery, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// Helper to shuffle an array
const shuffleArray = (array) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const NewArrivalShowcase = ({ newArrivalsData }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [minHeight, setMinHeight] = useState('auto');
  const gridWrapperRef = useRef(null);
  const navigate = useNavigate();

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.down("md"));

  // Number of products to show at once based on screen size
  const itemsPerPage = 4;

  // Process and shuffle products
  const allProducts = useMemo(() => {
    if (!newArrivalsData) return [];

    let products = [];
    Object.keys(newArrivalsData).forEach(brand => {
      const brandProducts = newArrivalsData[brand];
      if (brandProducts && brandProducts.length > 0) {
        // Pick random product
        const randomProduct = brandProducts[Math.floor(Math.random() * brandProducts.length)];
        products.push({ ...randomProduct, showcaseBrand: brand });
      }
    });

    return shuffleArray(products);
  }, [newArrivalsData]);

  // Group into pages
  const pages = useMemo(() => {
    const result = [];
    for (let i = 0; i < allProducts.length; i += itemsPerPage) {
      result.push(allProducts.slice(i, i + itemsPerPage));
    }
    return result;
  }, [allProducts, itemsPerPage]);

  const totalPages = pages.length;

  const handleNextPage = useCallback(() => {
    if (totalPages === 0) return;
    setFade(false);
    setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % totalPages);
      setFade(true);
    }, 500);
  }, [totalPages]);

  const handleSetPage = useCallback((index) => {
    if (index === currentIndex) return;
    setFade(false);
    setTimeout(() => {
      setCurrentIndex(index);
      setFade(true);
    }, 500);
  }, [currentIndex]);

  useEffect(() => {
    if (totalPages <= 1) return;
    const interval = setInterval(() => {
      handleNextPage();
    }, 5000);
    return () => clearInterval(interval);
  }, [handleNextPage, totalPages, currentIndex]);

  const currentProducts = pages[currentIndex] || [];

  useEffect(() => {
    if (gridWrapperRef.current && currentProducts.length === itemsPerPage) {
      setMinHeight(`${gridWrapperRef.current.offsetHeight}px`);
    }
  }, [currentProducts.length, itemsPerPage, isMobile, isTablet]);

  if (!allProducts.length) return null;

  return (
    <Box sx={{ mt: 3, mb: 4, px: { xs: 2, sm: 3 }, overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, flexDirection: { xs: "column", sm: "row" }, gap: { xs: 1.5, sm: 0 }, mb: 2 }}>
        <Box>
          <Typography
            sx={{
              fontFamily: "'Lora', serif",
              fontSize: { xs: "18px", md: "28px" },
              fontWeight: 500,
              color: "#111",
              mb: 0.5,
              letterSpacing: "-0.02em"
            }}
          >
            <Box component="span" sx={{ fontStyle: "italic", color: "#FF6B00" }}>Fresh drops</Box> from brands you follow
          </Typography>
        </Box>
        <Typography
          onClick={() => navigate("/brands/new-arrivals")}
          sx={{
            fontFamily: "Poppins-Medium",
            fontSize: { xs: "12px", md: "14px" },
            color: "#5abccb",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            "&:hover": { textDecoration: "underline" },
            alignSelf: { xs: "flex-end", sm: "auto" }
          }}
        >
          View All New Arrivals <ArrowForwardIosIcon sx={{ fontSize: "10px" }} />
        </Typography>
      </Box>

      {/* Dynamic Content area */}
      <Box ref={gridWrapperRef} sx={{ position: "relative", minHeight }}>
        <Grid
          container
          spacing={2}
          sx={{
            opacity: fade ? 1 : 0,
            transform: fade ? "translateY(0)" : "translateY(10px)",
            transition: "opacity 0.5s ease-in-out, transform 0.5s ease-in-out",
          }}
        >
          {currentProducts.map((product, idx) => (
            <Grid item xs={currentProducts.length === 2 ? 12 : 6} sm={currentProducts.length === 2 ? 12 : 6} md={currentProducts.length === 2 ? 6 : 3} key={`${product.product_id}-${idx}`}>
              <Box
                onClick={() => {
                  const slug = product.showcaseBrand.replace(/\s+/g, '-');
                  navigate(`/new-arrivals/${encodeURIComponent(slug)}`);
                }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  cursor: "pointer",
                  "&:hover .image-container": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 24px rgba(0,0,0,0.1)",
                  }
                }}
              >
                <Box
                  className="image-container"
                  sx={{
                    width: "100%",
                    aspectRatio: { xs: currentProducts.length === 2 ? "3/2" : "3/4", sm: "auto" },
                    height: { xs: "auto", sm: "360px", md: "460px" },
                    borderRadius: "16px",
                    overflow: "hidden",
                    position: "relative",
                    transition: "all 0.3s ease",
                    backgroundColor: "#F9F9F9",
                  }}
                >
                  <img
                    src={product?.image_url}
                    alt={product.title}
                    loading="lazy"
                    onError={(e) => {
                      const fallbackImage = product?.images?.[0];
                      if (fallbackImage && !e.target.dataset.triedFallback) {
                        e.target.dataset.triedFallback = "true";
                        e.target.src = fallbackImage;
                      } else {
                        e.target.onerror = null;
                        e.target.src = require("../../assets/images/no_image.png");
                      }
                    }}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  {/* Brand & Product Name Overlay */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      width: "100%",
                      background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)",
                      px: 2,
                      pt: 4,
                      pb: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.25
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Poppins-SemiBold",
                        fontSize: { xs: "13px", sm: "14px" },
                        color: "#FFF",
                        letterSpacing: "0.2px",
                        textTransform: "uppercase"
                      }}
                    >
                      {product.showcaseBrand}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Poppins-Regular",
                        fontSize: { xs: "12px", sm: "13px" },
                        color: "rgba(255,255,255,0.9)",
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                    >
                      {product.title}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Translucent Radio Dots Navigation */}
        {totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
              mt: 3
            }}
          >
            {pages.map((_, dotIdx) => (
              <Box
                key={dotIdx}
                onClick={() => handleSetPage(dotIdx)}
                sx={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "4px",
                  backgroundColor: currentIndex === dotIdx ? "#FF6B00" : "rgba(0,0,0,0.2)",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: currentIndex === dotIdx ? "#FF6B00" : "rgba(0,0,0,0.4)"
                  }
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NewArrivalShowcase;
